import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, ApiError } from "../lib/api";

const RESET_ERRORS: Record<string, string> = {
  invalid_token: "That reset code isn't valid.",
  token_expired: "That reset code has expired. Request a new one.",
  weak_password: "Choose a password with at least 10 characters.",
};

export function ResetPasswordPage() {
  const [params] = useSearchParams();
  // Two steps: request a code by email, then confirm with code + new password.
  const [email, setEmail] = useState("");
  const [requested, setRequested] = useState(false);
  const [token, setToken] = useState(params.get("token") ?? "");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  async function onRequest(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Always succeeds (no account enumeration); surface a neutral message.
      await api.requestPasswordReset(email.trim().toLowerCase());
    } catch {
      /* ignore — request endpoint never reveals account existence */
    } finally {
      setRequested(true);
      setSubmitting(false);
    }
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      await api.confirmPasswordReset(token.trim().toUpperCase(), password);
      setDone(true);
    } catch (err) {
      setError(
        err instanceof ApiError && RESET_ERRORS[err.code]
          ? RESET_ERRORS[err.code]
          : "Couldn't reset your password. Please try again.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (done) {
    return (
      <section className="page auth-page">
        <h1>Password reset</h1>
        <p>
          Your password has been updated. <Link to="/login">Sign in</Link>.
        </p>
      </section>
    );
  }

  return (
    <section className="page auth-page">
      <h1>Reset your password</h1>

      {!requested ? (
        <form className="auth-form" onSubmit={onRequest} noValidate>
          <p className="muted">
            Enter your email and we'll send a reset code if an account exists.
          </p>
          <label className="auth-label" htmlFor="reset-email">
            Email
            <input
              id="reset-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Sending…" : "Send reset code"}
          </button>
        </form>
      ) : (
        <form className="auth-form" onSubmit={onConfirm} noValidate>
          <p className="muted">
            If an account exists for {email || "that email"}, a reset code is on
            its way. Enter it below with a new password.
          </p>
          {error ? (
            <p className="auth-error" role="alert">
              {error}
            </p>
          ) : null}
          <label className="auth-label" htmlFor="reset-token">
            Reset code
            <input
              id="reset-token"
              inputMode="text"
              autoCapitalize="characters"
              required
              value={token}
              onChange={(e) => setToken(e.target.value)}
            />
          </label>
          <label className="auth-label" htmlFor="reset-password">
            New password
            <input
              id="reset-password"
              type="password"
              autoComplete="new-password"
              required
              minLength={10}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Resetting…" : "Reset password"}
          </button>
        </form>
      )}
    </section>
  );
}
